import classnames from "classnames";
import { DateTime } from "luxon";
import React, { useCallback, useContext, useMemo, useState } from "react";
import ReactCalendar, {
  CalendarTileProperties,
  ViewCallbackProperties,
} from "react-calendar";
import { FormattedMessage, useIntl } from "react-intl";

import IconArrowLeft from "../../../public/svg/icon-arrow-left.svg";
import IconArrowRight from "../../../public/svg/icon-arrow-right.svg";
import { ThemeContext } from "../../contexts/ThemeContext";
import { getCheapestPriceOfMonth } from "../../ecommerce/product";
import { getStartingDate } from "../../ecommerce/schedule";
import { Locale } from "../../lib/i18n";
import {
  DynamicPricingCalendarScheduleDate,
  ExtendedVariant,
} from "../../types/shopify-components";
import { paragraph3 } from "../../utils/styles";
import Money from "../Money";

export interface DynamicPricingCalendarProps {
  className?: string;
  defaultValue?: DateTime;
  handleDaySelect: (date: DateTime) => void;
  schedule: DynamicPricingCalendarScheduleDate[];
  showPrices?: boolean;
  variants: ExtendedVariant[];
}

// TODO: Rename component
const DynamicPricingCalendar: React.FC<DynamicPricingCalendarProps> = ({
  className,
  defaultValue,
  handleDaySelect,
  schedule,
  showPrices,
  variants,
}) => {
  const startingDate = getStartingDate(schedule);
  const initialStartingDate = defaultValue?.startOf("month") ?? startingDate;
  const maxDate = schedule
    .reduce((a, b) => (a.date > b.date ? a : b))
    /**
     *  Note: The next month button can become disabled if the latest schedule date
     *  is equal to the max date. This is the way react-calendar is implemented.
     *
     *  For example with the schedule ["2021-02-28T00:00:00", "2021-03-01T00:00:00"],
     *  maxDate should be at least "2021-03-01T00:00:01"
     */
    .date.plus({ days: 1 });

  const { styles } = useContext(ThemeContext);
  const { locale, formatMessage } = useIntl();
  const [activeMonth, setActiveMonth] = useState<number>(
    initialStartingDate.month,
  );
  const [activeMonthHasSpecialEvent, setActiveMonthHasSpecialEvent] =
    useState(false);

  const validityDateLookup: number[] = useMemo(() => {
    return variants.length
      ? variants.reduce<number[]>((lookup, { validityDate }) => {
          if (validityDate?.isValid) {
            return [...lookup, validityDate.startOf("day").toMillis()];
          }
          return lookup;
        }, [])
      : [];
  }, [variants]);

  const hasPricingData = validityDateLookup.length > 0;

  const scheduleLookup = useMemo(() => {
    setActiveMonthHasSpecialEvent(false);
    return new Map(
      schedule.map(({ date, specialEvent }) => {
        const hasSpecialEvent = !!specialEvent?.[locale as Locale]?.title;
        if (hasSpecialEvent && date.month == activeMonth) {
          setActiveMonthHasSpecialEvent(true);
        }
        return [date.toMillis(), { hasSpecialEvent }];
      }),
    );
  }, [schedule, activeMonth, locale]);

  const scheduleTimestamps = useMemo(
    () => Array.from(scheduleLookup.keys()),
    [scheduleLookup],
  );

  const [cheapestPriceOfMonth, setCheapestPriceOfMonth] = useState(
    variants.length
      ? getCheapestPriceOfMonth(
          initialStartingDate,
          variants,
          scheduleTimestamps,
        )
      : 0,
  );

  const getDateAvailability = useCallback(
    (
      tileProps: CalendarTileProperties,
    ): "not-relevant" | "available" | "not-available" => {
      const date = DateTime.fromJSDate(tileProps.date);
      const isOutOfRange = startingDate > date || date > maxDate;

      if (isOutOfRange) {
        return "not-relevant";
      }

      if (
        (validityDateLookup.length &&
          !validityDateLookup.includes(date.toMillis())) ||
        !scheduleLookup.has(date.toMillis())
      ) {
        return "not-available";
      }

      return "available";
    },
    [validityDateLookup, scheduleLookup, maxDate, startingDate],
  );

  const isDailyPricingAvailable = useCallback(
    (tileProps: CalendarTileProperties): ExtendedVariant | undefined => {
      const date = DateTime.fromJSDate(tileProps.date);
      return variants?.find(
        variant => variant.validityDate?.toMillis() === date.toMillis(),
      );
    },
    [variants],
  );

  const isSpecialEventOnDay = useCallback(
    (tileProps: CalendarTileProperties): boolean => {
      const tileDateTimeStamp = DateTime.fromJSDate(tileProps.date).toMillis();
      return scheduleLookup.get(tileDateTimeStamp)?.hasSpecialEvent ?? false;
    },
    [scheduleLookup],
  );

  const tileContent = useCallback(
    (tileProps: CalendarTileProperties): JSX.Element | null => {
      const availability = getDateAvailability(tileProps);
      const variant = isDailyPricingAvailable(tileProps);

      if (availability !== "available" || !variant || showPrices === false) {
        return null;
      } else {
        return (
          <span className="db tc mt2 f8">
            <Money value={Number(variant.price.amount)} />
          </span>
        );
      }
    },
    [isDailyPricingAvailable, getDateAvailability, showPrices],
  );

  const tileClassName = useCallback(
    (tileProps: CalendarTileProperties): string => {
      const availability = getDateAvailability(tileProps);

      const isTouch = Boolean("ontouchstart" in window);
      const hover = !isTouch ? "hover" : "";

      if (availability === "not-relevant") {
        return classnames("not-relevant", hover);
      } else if (availability === "not-available") {
        return classnames("not-available", hover);
      } else {
        const variant = isDailyPricingAvailable(tileProps);
        const price = variant ? Number(variant.price.amount) : undefined;

        const specialEvent = isSpecialEventOnDay(tileProps)
          ? "special-event"
          : "";

        return classnames(availability, hover, specialEvent, {
          "cheaper-price": price === cheapestPriceOfMonth,
        });
      }
    },
    [
      cheapestPriceOfMonth,
      isDailyPricingAvailable,
      getDateAvailability,
      isSpecialEventOnDay,
    ],
  );

  const tileDisabled = useCallback(
    (tileProps: CalendarTileProperties): boolean => {
      if (!variants.length || (!hasPricingData && variants.length > 1)) {
        return true;
      }

      if (DateTime.fromJSDate(tileProps.date).month !== activeMonth) {
        return true;
      }

      return getDateAvailability(tileProps) !== "available";
    },
    [activeMonth, getDateAvailability, variants.length, hasPricingData],
  );

  const onActiveStartDateChange = useCallback(
    (viewCallbackProps: ViewCallbackProperties): void => {
      if (variants.length) {
        const activeStartDate = DateTime.fromJSDate(
          viewCallbackProps.activeStartDate,
        );
        setActiveMonth(activeStartDate.month);
        setCheapestPriceOfMonth(
          getCheapestPriceOfMonth(
            activeStartDate,
            variants,
            scheduleTimestamps,
          ),
        );
      }
    },
    [scheduleTimestamps, variants],
  );

  const onClickDay = useCallback(
    (date: Date) => {
      const clickedDay = DateTime.fromJSDate(date);
      handleDaySelect(clickedDay);
    },
    [handleDaySelect],
  );

  return (
    <section data-testid="dynamic-pricing-calendar-container">
      <ReactCalendar
        className={classnames(className, {
          "has-pricing-data": hasPricingData,
        })}
        defaultView="month"
        minDetail="month"
        locale={locale}
        tileClassName={tileClassName}
        tileContent={tileContent}
        tileDisabled={tileDisabled}
        next2Label={null}
        prev2Label={null}
        nextLabel={
          <span className="flex items-center justify-center icon-wrapper-large svg-icon stroke-current-color fill-transparent pointer hover-pink ph3-l">
            <IconArrowRight />
          </span>
        }
        prevLabel={
          <span className="flex items-center justify-center icon-wrapper-large svg-icon stroke-current-color fill-transparent pointer hover-pink ph3-l">
            <IconArrowLeft />
          </span>
        }
        navigationAriaLabel={formatMessage({ id: "calendar.selectedMonth" })}
        nextAriaLabel={formatMessage({ id: "calendar.nextMonth" })}
        prevAriaLabel={formatMessage({ id: "calendar.prevMonth" })}
        defaultActiveStartDate={initialStartingDate.toJSDate()}
        minDate={startingDate.toJSDate()}
        maxDate={maxDate.toJSDate()}
        onClickDay={onClickDay}
        onActiveStartDateChange={onActiveStartDateChange}
        defaultValue={defaultValue?.toJSDate()}
      />
      <div className="mt3 ph3-ns">
        <ul className="list ma0 pa0 nb3">
          <li
            className={`${paragraph3} dib ph3 pv2 black bg-light-gray tc lh-solid br-pill mr3 mb3`}
          >
            <FormattedMessage id="calendar.isNotAvailable" />
          </li>
          <li
            className={`${paragraph3} dib ph3 pv2 black bg-bright-turquoise tc lh-solid br-pill mr3 mb3`}
            data-testid="dynamic-pricing-calendar-park-is-available"
          >
            <FormattedMessage id="calendar.isAvailable" />
          </li>
          {hasPricingData && cheapestPriceOfMonth && (
            <li
              className={`${paragraph3} dib ph3 pv2 black bg-turquoise tc lh-solid br-pill mb3`}
              data-testid="dynamic-pricing-calendar-cheapest-price"
            >
              <FormattedMessage id="calendar.isAvailableCheapestPrice" />
            </li>
          )}
          {activeMonthHasSpecialEvent && (
            <li
              className="flex items-center"
              data-testid="dynamic-pricing-calendar-special-event"
            >
              <span
                className="bg-pink br-100 dib flex-shrink-0"
                aria-hidden="true"
                style={{ width: "0.5rem", height: "0.5rem" }}
              />
              <span className={`${styles.paragraph3} ml2`}>
                <FormattedMessage id="calendar.hasSpecialEvents" />
              </span>
            </li>
          )}
        </ul>
      </div>
    </section>
  );
};

export default DynamicPricingCalendar;
